/**
 * LiveDashboardV2 - Main container for the live proctoring dashboard
 * Shows real-time student exam status with sorting, filtering, and actions
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Spinner, Form } from 'react-bootstrap'
import { useDebounce } from 'use-debounce'
import log from 'loglevel'
import fileDownload from 'js-file-download'
import * as constants from 'Constants'
import * as actions from 'Store/Course/Instructor/Labs/Actions'
import { loadCoursesListRequest } from 'Screens_old/PublicCourses/Common/Actions/Actions'
import { loadCoursesListRequest as instructor_loadCoursesListRequest } from 'Store/MyDashboard/Instructor/Actions'
import httpService from 'services/httpService'
import displayGlobalMessage from 'Utils/DisplayGlobalMessage'
import useInstructorExamWebSocket from 'hooks/useInstructorExamWebSocket'
import { RefreshProvider } from './RefreshContext'
import DashboardHeader from './DashboardHeader'
import StatsCards from './StatsCards'
import ControlsRow from './ControlsRow'
import ColumnToggle from './ColumnToggle'
import DashboardTable from './DashboardTable'
import Pagination from '../Pagination'
import EndExamChoiceModal, { fetchRunningStudents } from '../Modals/EndExamChoiceModal'
import styles from '../../LabsV2.module.css'

// Session storage keys
const STORAGE_KEYS = {
  SORT_FIELD: 'liveDashboardV2_sortField',
  SORT_ORDER: 'liveDashboardV2_sortOrder',
  VISIBLE_COLUMNS: 'liveDashboardV2_visibleColumns',
  PAGE_SIZE: 'liveDashboardV2_pageSize'
}

// Default page size
const DEFAULT_PAGE_SIZE = 25

// Coalescing window for WebSocket-driven refetches. Student joins arrive in
// bursts at exam start, so refetches are debounced and capped by a max wait so
// a steady trickle of events still reconciles.
const WS_REFETCH_DEBOUNCE_MS = 1000
const WS_REFETCH_MAX_WAIT_MS = 5000

const LiveDashboardV2 = () => {
  const { courseId, examId } = useParams()
  const dispatch = useDispatch()

  // Auto-end modal state (triggered when header countdown hits zero)
  const [showAutoEndModal, setShowAutoEndModal] = useState(false)
  const [autoEndRunning, setAutoEndRunning] = useState([])
  const [autoEndLoading, setAutoEndLoading] = useState(false)

  // Exam info state
  const [examTitle, setExamTitle] = useState('')
  const [ipRange, setIpRange] = useState('')
  const [labDuration, setLabDuration] = useState('')
  const [labAdditionalTime, setLabAdditionalTime] = useState('')
  const [timeLeft, setTimeLeft] = useState(null)

  // Course name for breadcrumb
  const [courseName, setCourseName] = useState('')
  const instructorCourses = useSelector((state) => state.instructorCoursesMap)
  const courses = useSelector((state) => state.publicCoursesMap)

  useEffect(() => {
    dispatch(loadCoursesListRequest())
    dispatch(instructor_loadCoursesListRequest())
  }, [dispatch])

  useEffect(() => {
    let tempCourse
    if (courses?.length > 0) {
      tempCourse = courses.find((item) => item.id === Number(courseId))
      if (tempCourse) setCourseName(tempCourse.title)
    }
    if (!tempCourse && instructorCourses?.length > 0) {
      tempCourse = instructorCourses.find((item) => item.id === Number(courseId))
      if (tempCourse) setCourseName(tempCourse.title)
    }
  }, [courses, instructorCourses, courseId])

  // Student data state
  const [students, setStudents] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [ongoingStudentsCount, setOngoingStudentsCount] = useState(0)
  const [finishedStudentsCount, setFinishedStudentsCount] = useState(0)

  // Loading state
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const [clockOffset, setClockOffset] = useState(0)

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(
    parseInt(sessionStorage.getItem(STORAGE_KEYS.PAGE_SIZE)) || DEFAULT_PAGE_SIZE
  )

  // Sorting state
  const [sortField, setSortField] = useState(
    sessionStorage.getItem(STORAGE_KEYS.SORT_FIELD) || 'is_approved'
  )
  const [sortOrder, setSortOrder] = useState(
    parseInt(sessionStorage.getItem(STORAGE_KEYS.SORT_ORDER)) || 1
  )

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.VISIBLE_COLUMNS)
    return stored ? JSON.parse(stored) : []
  })

  // Selection state
  const [selectedRows, setSelectedRows] = useState([])

  // Ref to track if search changed (to reset page)
  const prevSearchRef = useRef(debouncedSearchQuery)

  const studentsRef = useRef([])
  useEffect(() => {
    studentsRef.current = students
  }, [students])

  // Persist settings to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.SORT_FIELD, sortField)
    sessionStorage.setItem(STORAGE_KEYS.SORT_ORDER, sortOrder.toString())
  }, [sortField, sortOrder])

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.VISIBLE_COLUMNS, JSON.stringify(visibleColumns))
  }, [visibleColumns])

  // Reset to page 1 when search query changes
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearchQuery) {
      setCurrentPage(1)
      prevSearchRef.current = debouncedSearchQuery
    }
  }, [debouncedSearchQuery])

  // Fetch exam details
  const fetchExamDetails = useCallback(async () => {
    if (!examId) return
    try {
      const url = `${constants.base_url}/lab/all/${examId}?courseId=${courseId}`
      const response = await httpService.get(url)
      setExamTitle(response.data.lab?.title || '')
      setIpRange(response.data.lab?.ip_range || '')
      setLabDuration(response.data.lab?.duration || '')
      setLabAdditionalTime(response.data.lab?.additional_time || '')
      setTimeLeft(response.data.lab?.time_left ?? null)
    } catch (error) {
      log.error('Error fetching exam details:', error)
      if (error?.response?.status === 404) {
        displayGlobalMessage("Couldn't find the lab", 'error')
      }
    }
  }, [examId, courseId])

  useEffect(() => {
    fetchExamDetails()
  }, [fetchExamDetails])

  // Fetch student data
  const fetchStudents = useCallback(async (page = currentPage) => {
    try {
      setIsRefreshing(true)
      const querySortOrder = sortOrder === -1 ? '-' : ''

      const url = `${constants.base_url}/exam/${examId}/proctor?page=${page}&page_size=${pageSize}&search_query=${debouncedSearchQuery}&sort=${querySortOrder}${sortField}`

      const response = await httpService.get(url)
      setStudents(response.data.student_exam?.results || [])
      setTotalCount(response.data.student_exam?.count || 0)
      setOngoingStudentsCount(response.data.ongoing_students_count || 0)
      setFinishedStudentsCount(response.data.finished_students_count || 0)
      if (response.data.server_now) {
        setClockOffset(Date.now() - new Date(response.data.server_now).getTime())
      }
      setLoading(false)
    } catch (error) {
      log.error('Error fetching students:', error)
      displayGlobalMessage('Failed to refresh data', 'error')
    } finally {
      setIsRefreshing(false)
    }
  }, [examId, debouncedSearchQuery, sortField, sortOrder, pageSize, currentPage])

  const refetchTimerRef = useRef(null)
  const refetchDeadlineRef = useRef(null)

  const scheduleRefetch = useCallback(() => {
    const now = Date.now()
    if (refetchDeadlineRef.current == null) {
      refetchDeadlineRef.current = now + WS_REFETCH_MAX_WAIT_MS
    }
    const delay = Math.min(
      WS_REFETCH_DEBOUNCE_MS,
      Math.max(0, refetchDeadlineRef.current - now)
    )
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current)
    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null
      refetchDeadlineRef.current = null
      fetchStudents(currentPage)
    }, delay)
  }, [fetchStudents, currentPage])

  // Drop a pending refetch whenever the page/query it was scheduled for changes,
  // so it cannot land after — and overwrite — the fetch that change triggers.
  useEffect(() => () => {
    if (refetchTimerRef.current) {
      clearTimeout(refetchTimerRef.current)
      refetchTimerRef.current = null
      refetchDeadlineRef.current = null
    }
  }, [scheduleRefetch])

  // WebSocket dashboard update handler
  const handleDashboardUpdate = useCallback((message) => {
    const { event, student, counts } = message

    if (message.server_now) {
      setClockOffset(Date.now() - new Date(message.server_now).getTime())
    }

    // Always update counts from the server
    if (counts) {
      setOngoingStudentsCount(counts.ongoing_students_count)
      setFinishedStudentsCount(counts.finished_students_count)
    }

    if (event === 'time_modified_all' || event === 'lab_state_changed') {
      // Full refetch — too many students to send over WS, or lab-level state changed
      fetchStudents(currentPage)
      fetchExamDetails()
      return
    }

    if (!student) return

    if (event === 'student_joined') {
      // Show the joiner immediately when there is room, but always reconcile:
      // the server orders flagged students first and paginates, so a local
      // append cannot decide page membership or position on its own.
      setStudents(prev => {
        const exists = prev.some(s => s.student_id === student.student_id)
        if (exists) {
          return prev.map(s => s.student_id === student.student_id ? student : s)
        }
        if (currentPage === 1 && prev.length < pageSize) {
          return [...prev, student]
        }
        return prev
      })
      scheduleRefetch()
    } else {
      // student_approved, student_flagged, student_marked_left,
      // student_unmarked_left, time_modified, ip_collision_resolved
      const exists = studentsRef.current.some(
        s => s.student_id === student.student_id
      )
      if (exists) {
        setStudents(prev =>
          prev.map(s => s.student_id === student.student_id ? student : s)
        )
      }
      scheduleRefetch()
    }
  }, [currentPage, pageSize, scheduleRefetch, fetchStudents, fetchExamDetails])

  // Connect instructor WebSocket
  const { isConnected: isWebSocketConnected } = useInstructorExamWebSocket({
    labId: examId,
    onDashboardUpdate: handleDashboardUpdate,
  })

  const hasConnectedOnceRef = useRef(false)
  useEffect(() => {
    if (isWebSocketConnected) {
      if (hasConnectedOnceRef.current) {
        fetchStudents(currentPage)
        fetchExamDetails()
      }
      hasConnectedOnceRef.current = true
    }
  }, [isWebSocketConnected, fetchStudents, fetchExamDetails, currentPage])

  // Initial fetch and when dependencies change
  useEffect(() => {
    fetchStudents(currentPage)
  }, [examId, debouncedSearchQuery, sortField, sortOrder, pageSize, currentPage])

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
  }

  // Handle sort
  const handleSort = (field, order) => {
    setSortField(field)
    setSortOrder(order)
    setCurrentPage(1) // Reset to page 1 when sorting changes
  }

  // Handle row selection
  const handleSelectRow = (studentId) => {
    setSelectedRows(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    )
  }

  // Handle select all (only for current page)
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(students.map(s => s.student_id))
    } else {
      setSelectedRows([])
    }
  }

  // Handle approve with IP action choice
  const handleApprove = async (studentId, type, ipAction = 'accept_new') => {
    try {
      const response = await httpService.put(
        `${constants.base_url}/exam/${examId}/proctor/approve/${studentId}?type=${type}&ip_action=${ipAction}`
      )
      displayGlobalMessage('Approved Successfully', 'success')

      // Update local state
      setStudents(prev =>
        prev.map(student =>
          student.student_id === studentId
            ? response.data.student_exam
            : student
        )
      )
    } catch (error) {
      if (error.response?.status === 403) {
        displayGlobalMessage("Exam has ended, Can't approve now", 'error')
      } else {
        displayGlobalMessage("Some error has occurred, Can't approve now", 'error')
        log.error(error)
      }
    }
  }

  // Handle mark student as left (frees their IP for reallocation)
  const handleMarkAsLeft = async (studentId) => {
    if (!window.confirm('Mark this student as left? This will free their IP for another student.')) {
      return
    }
    try {
      const response = await httpService.put(
        `${constants.base_url}/exam/${examId}/proctor/mark_left/${studentId}`
      )
      displayGlobalMessage(response.data.message || 'Student marked as left', 'success')

      // Update local state
      setStudents(prev =>
        prev.map(student =>
          student.student_id === studentId
            ? response.data.student_exam
            : student
        )
      )
    } catch (error) {
      displayGlobalMessage('Failed to mark student as left', 'error')
      log.error(error)
    }
  }

  // Handle manual flag (block student)
  const handleManualFlag = async (studentId) => {
    const reason = window.prompt('Enter reason for flagging this student (optional):')
    if (reason === null) return // User cancelled

    try {
      const response = await httpService.put(
        `${constants.base_url}/exam/${examId}/proctor/manual_flag/${studentId}`,
        { reason: reason || 'Flagged by instructor' }
      )
      displayGlobalMessage(response.data.message || 'Student flagged', 'success')

      setStudents(prev =>
        prev.map(student =>
          student.student_id === studentId
            ? response.data.student_exam
            : student
        )
      )
    } catch (error) {
      displayGlobalMessage('Failed to flag student', 'error')
      log.error(error)
    }
  }

  // Handle undo mark as left
  const handleUndoMarkLeft = async (studentId) => {
    try {
      const response = await httpService.put(
        `${constants.base_url}/exam/${examId}/proctor/undo_mark_left/${studentId}`
      )
      displayGlobalMessage(response.data.message || 'Student unmarked', 'success')

      // Update local state
      setStudents(prev =>
        prev.map(student =>
          student.student_id === studentId
            ? response.data.student_exam
            : student
        )
      )
    } catch (error) {
      displayGlobalMessage('Failed to undo mark as left', 'error')
      log.error(error)
    }
  }

  // Download CSV
  const handleDownloadCSV = async () => {
    try {
      const url = `${constants.base_url}/exam/${examId}/proctor/downloadcsv`
      const response = await httpService.get(url)
      fileDownload(response.data, `${examTitle}_Proctor.csv`, 'text/csv')
    } catch (error) {
      log.error('Error downloading CSV:', error)
      displayGlobalMessage('Error downloading CSV', 'error')
    }
  }

  // Header countdown reached zero — open the End Exam choice modal.
  // Always fetches fresh data so it never disagrees with the dashboard.
  const handleAutoEnd = useCallback(() => {
    setAutoEndRunning([])
    setShowAutoEndModal(true)
    const now = Date.now() - clockOffset
    fetchRunningStudents(examId, now).then(setAutoEndRunning)
  }, [clockOffset, examId])

  const handleAutoEndConfirm = async (mode) => {
    setAutoEndLoading(true)
    try {
      await dispatch(actions.endExam(examId, mode))
      setShowAutoEndModal(false)
      fetchStudents(currentPage)
      fetchExamDetails()
    } catch (error) {
      log.error('Error ending exam:', error)
    } finally {
      setAutoEndLoading(false)
    }
  }

  // Calculate stats
  const totalStudents = ongoingStudentsCount + finishedStudentsCount
  const unapprovedCount = students.filter(s => !s.is_approved && !s.has_left).length
  const allSelected = selectedRows.length === students.length && students.length > 0
  const totalPages = Math.ceil(totalCount / pageSize)

  return (
    <RefreshProvider
      interval={isWebSocketConnected ? null : 30}
      onRefresh={() => fetchStudents(currentPage)}
      isExternallyLoading={isRefreshing}
    >
      <div className={styles.liveDashboardContainer}>
        <DashboardHeader
          courseId={courseId}
          examId={examId}
          examTitle={examTitle}
          courseName={courseName}
        />

        <div className={styles.liveDashboardContent}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <StatsCards
                totalStudents={totalStudents}
                ipRanges={ipRange}
                unapprovedCount={unapprovedCount}
              />

              <ControlsRow
                examId={examId}
                onTimeModified={() => { fetchStudents(currentPage); fetchExamDetails() }}
                onDownloadCSV={handleDownloadCSV}
                isLoading={isRefreshing}
                isWebSocketConnected={isWebSocketConnected}
                labDuration={labDuration}
                labAdditionalTime={labAdditionalTime}
                timeLeft={timeLeft}
                onComplete={handleAutoEnd}
              />

              <div className={styles.dashboardTableHeader}>
                <ColumnToggle
                  visibleColumns={visibleColumns}
                  onColumnsChange={setVisibleColumns}
                />
                <div className={styles.dashboardSearchSection}>
                  <Form.Control
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={styles.dashboardSearchInput}
                  />
                </div>
              </div>

              <div className={styles.dashboardTableContainer}>
                <DashboardTable
                  students={students}
                  examId={examId}
                  clockOffset={clockOffset}
                  visibleColumns={visibleColumns}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  selectedRows={selectedRows}
                  onSelectRow={handleSelectRow}
                  onSelectAll={handleSelectAll}
                  allSelected={allSelected}
                  onApprove={handleApprove}
                  onMarkAsLeft={handleMarkAsLeft}
                  onUndoMarkLeft={handleUndoMarkLeft}
                  onManualFlag={handleManualFlag}
                  onDataRefresh={() => fetchStudents(currentPage)}
                />
              </div>

              {totalCount > pageSize && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      </div>
      <EndExamChoiceModal
        show={showAutoEndModal}
        onHide={() => setShowAutoEndModal(false)}
        onEndForAll={() => handleAutoEndConfirm('hard')}
        onLetContinue={() => handleAutoEndConfirm('soft')}
        runningStudents={autoEndRunning}
        isLoading={autoEndLoading}
      />
    </RefreshProvider>
  )
}

export default LiveDashboardV2
